import { useCallback, useState } from 'react';
import { useApiStore, useStore } from '@core/store';
import { apiClient } from '@core/api/ApiClientFactory';
import { ActiveLicenses } from '@core/types';

export const useAppInitialization = () => {
    const [isLoading, setIsLoading] = useState(false);
    const {
        loadOrganisation,
        loadShopIds,
        loadComponentIds,
        loadGatewayIds,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds,
        loadOperationModes,
        loadShopAutomationIds,
        clearApiData
    } = useApiStore();

    const loadAllData = useCallback(
        async (orgId?: string) => {
            const { user, hasLicense } = useStore.getState();
            const resolvedOrgId = orgId ?? user?.organisation?.id;
            const validOrg = user?.organisationClaims?.find((o) => o.id === resolvedOrgId);
            if (!validOrg) {
                return;
            }

            apiClient.setOrganisationId(validOrg.id);

            // Clear stale data so components don't briefly show data from the previous org.
            clearApiData();

            await Promise.all([loadOrganisation(), loadShopIds()]);

            const idPromises: Promise<any>[] = [loadComponentIds(), loadGatewayIds()];
            if (hasLicense(ActiveLicenses.Rfid)) idPromises.push(loadTrolleyShelterIds());
            if (hasLicense(ActiveLicenses.ShopAutomation))
                idPromises.push(
                    loadOperationModes(),
                    loadShopAutomationIds(),
                    loadZoneTrackerIds(),
                    loadLineTrackerIds()
                );
            await Promise.all(idPromises);
        },
        [
            loadOrganisation,
            loadShopIds,
            loadComponentIds,
            loadGatewayIds,
            loadTrolleyShelterIds,
            loadZoneTrackerIds,
            loadLineTrackerIds,
            loadOperationModes,
            loadShopAutomationIds,
            clearApiData
        ]
    );

    const initializeApp = useCallback(
        async (orgId?: string) => {
            setIsLoading(true);
            try {
                await loadAllData(orgId);
                console.log('App initialization completed successfully');
            } catch (error) {
                console.error('App initialization failed:', error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [loadAllData]
    );

    const refreshAppData = useCallback(
        async (orgId?: string) => {
            try {
                await loadAllData(orgId);
            } catch (error) {
                console.error('Failed to refresh app data after org change:', error);
                throw error;
            }
        },
        [loadAllData]
    );

    return { initializeApp, refreshAppData, isLoading };
};
